import { API_CONFIG } from '../config/config.js';
import { detectDeviceClass } from '@/lib/deviceSupport';

const API_BASE_URL = API_CONFIG.BASE_URL;

const request = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('token');
    const config = {
        headers: { 'Content-Type': 'application/json', ...options.headers },
        ...options,
    };
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    try {
        const response = await fetch(url, config);
        if (!response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.indexOf('application/json') !== -1) {
                const errorData = await response.json();
                if (response.status === 429 && errorData.upgradeRequired) {
                    window.dispatchEvent(new CustomEvent('echohire:plan-limit', { detail: errorData }));
                }
                // A lapsed subscription is not a per-action limit — it blocks
                // every write until the bill clears, so it gets its own banner
                // rather than being mistaken for hitting a plan cap.
                if (response.status === 403 && errorData.companyBlocked) {
                    window.dispatchEvent(new CustomEvent('echohire:company-blocked', { detail: errorData }));
                }
                // Attach extra fields (code, existingUser, etc.) to the error so callers can inspect them
                const err = new Error(errorData.message || `HTTP error! status: ${response.status}`);
                err.status = response.status;
                err.code = errorData.code;
                err.data = errorData;
                throw err;
            } else {
                const errorText = await response.text();
                const err = new Error(errorText || `HTTP error! status: ${response.status}`);
                err.status = response.status;
                throw err;
            }
        }
        return response.status === 204 ? null : response.json();
    } catch (error) {
        console.error(`API Client Error: ${error.message}`);
        throw error;
    }
};

export const transcribeAudio = async (audioBlob) => {
    const url = `${API_BASE_URL}/interview/transcribe`;
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('audio', audioBlob, 'interview-response.webm');
    const headers = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    try {
        const response = await fetch(url, { method: 'POST', headers, body: formData });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        return response.json();
    } catch (error) {
        console.error(`API Client Error: ${error.message}`);
        throw error;
    }
};

export const register = (userData) => request('/auth/register', { method: 'POST', body: JSON.stringify(userData) });
export const login = (credentials) => request('/auth/login', { method: 'POST', body: JSON.stringify(credentials) });

export const createTemplate = (templateData) => request('/templates', { method: 'POST', body: JSON.stringify(templateData) });
export const getTemplates = () => request('/templates');
// Extracts the skills a job description implies, without saving anything.
export const analyseJobDescription = (jobDescription) =>
  request('/templates/analyse-jd', { method: 'POST', body: JSON.stringify({ jobDescription }) });
export const updateTemplate = (templateId, templateData) => request(`/templates/${templateId}`, { method: 'PUT', body: JSON.stringify(templateData) });
export const deleteTemplate = (templateId) => request(`/templates/${templateId}`, { method: 'DELETE' });
export const generateJobDescription = ({ roleTitle, experienceLevel = '', requiredSkills = '', mustHaveRequirements = '', additionalContext = '' }) =>
  request('/templates/generate-jd', { method: 'POST', body: JSON.stringify({ roleTitle, experienceLevel, requiredSkills, mustHaveRequirements, additionalContext }) });

export const getCandidates = () => request('/users?role=candidate');
export const getUsers = (role) => {
  const url = role ? `/users?role=${role}` : '/users';
  return request(url);
};
export const updateUser = (userId, data) => request(`/users/${userId}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteUser = (userId) => request(`/users/${userId}`, { method: 'DELETE' });

export const createSession = (sessionData) => request('/interview/sessions', { method: 'POST', body: JSON.stringify(sessionData) });
export const getSchedulableRound = (templateId, candidateId, processId) => {
  let url = `/interview/schedulable-round?templateId=${encodeURIComponent(templateId)}&candidateId=${encodeURIComponent(candidateId)}`;
  if (processId) url += `&processId=${encodeURIComponent(processId)}`;
  return request(url);
};
export const getSessionByLink = (uniqueLink) => request(`/interview/sessions/${uniqueLink}`);
export const submitResponse = (sessionId, responseData) => request(`/interview/sessions/${sessionId}/responses`, { method: 'POST', body: JSON.stringify(responseData) });
export const getMySessions = (page = 1, limit = 20) =>
  request(`/interview/sessions/my-sessions?page=${page}&limit=${limit}`);
export const getAllSessions = (page = 1, limit = 10, status, templateId, q) => {
    let url = `/interview/sessions?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    if (templateId) url += `&templateId=${templateId}`;
    if (q) url += `&q=${encodeURIComponent(q)}`;
    return request(url);
};
export const updateSession = (sessionId, sessionData) => request(`/interview/sessions/${sessionId}`, { method: 'PUT', body: JSON.stringify(sessionData) });
export const cancelSession = (sessionId, reason) => request(`/interview/sessions/${sessionId}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) });
export const getCompletedSessions = (page = 1, limit = 5, status, templateId, dateFrom, dateTo, roundNumber) => {
    let url = `/interview/sessions/completed?page=${page}&limit=${limit}`;
    if (status && status !== 'all') url += `&status=${status}`;
    if (templateId) url += `&templateId=${templateId}`;
    if (dateFrom) url += `&dateFrom=${encodeURIComponent(dateFrom)}`;
    if (dateTo) url += `&dateTo=${encodeURIComponent(dateTo)}`;
    if (roundNumber) url += `&roundNumber=${encodeURIComponent(roundNumber)}`;
    return request(url);
};

/**
 * Fetches the full session details for an admin, including ideal answers.
 * @param {string} sessionId The ID of the session.
 */
export const getSessionDetailsForAdmin = (sessionId) => request(`/interview/sessions/${sessionId}/details`);

/**
 * Fetches responses for a session.
 * By default (no limit arg) the backend returns ALL responses in one shot so the
 * admin report page always shows complete data regardless of question count.
 * Pass an explicit limit > 0 only if you need paginated results.
 */
export const getSessionResponses = (sessionId, page = 1, limit = 0) => {
    const params = new URLSearchParams({ page });
    if (limit > 0) params.set('limit', limit);
    return request(`/reports/${sessionId}/responses?${params}`);
};
export const generateReport = (sessionId, trackingData = {}) => request(`/reports/${sessionId}`, {
    method: 'POST',
    body: JSON.stringify(trackingData),
});
export const getReport = (sessionId) => request(`/reports/${sessionId}`);
export const regenerateReportFromAudio = (sessionId) => request(`/reports/${sessionId}/regenerate-from-audio`, {
    method: 'POST',
});

export const markSessionCompletedOrTerminated = (sessionId, data) =>
  request(`/interview/sessions/${sessionId}/complete`, { method: 'POST', body: JSON.stringify(data) });

/**
 * Called by the candidate after all responses are submitted.
 * Generates the AI report server-side with proctoring tracking data.
 * Accessible to candidates (no admin permission required).
 */
export const finalizeInterview = (sessionId, trackingData = {}) =>
  request(`/interview/sessions/${sessionId}/finalize`, { method: 'POST', body: JSON.stringify(trackingData) });

/**
 * Called when the first question is displayed — transitions status: scheduled → in_progress
 *
 * Carries the browser's own device classification. The server classifies the
 * User-Agent independently and refuses either way, so this header is a second
 * signal rather than the authority — it exists because a User-Agent alone
 * cannot tell an iPad from a Mac (iPadOS reports desktop Safari), while the
 * browser can, via its touch points.
 */
export const markSessionStarted = (sessionId) =>
  request(`/interview/sessions/${sessionId}/start`, {
    method: 'POST',
    headers: { 'X-Device-Class': detectDeviceClass() },
  });

/**
 * Called via fetch+keepalive when the candidate closes the tab.
 * Uses keepalive so the request completes even during page unload.
 */
export const abandonSessionBeacon = (sessionId) => {
  const token = localStorage.getItem('token');
  return fetch(`${API_BASE_URL}/interview/sessions/${sessionId}/abandon`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({}),
  });
};

/**
 * Submits a decision for an interview session.
 * @param {string} sessionId - The ID of the interview session.
 * @param {'approved' | 'rejected'} decision - The decision status.
 * @param {string} [comments] - Optional feedback comments.
 */
export const submitDecision = (sessionId, { decision, comments }) => 
  request(`/interview/sessions/${sessionId}/decision`, {
    method: 'POST',
    body: JSON.stringify({ decision, comments }),
  });

/**
 * Handles exporting a report as either CSV or PDF and triggers a file download.
 * @param {string} sessionId - The ID of the interview session.
 * @param {'csv' | 'pdf'} type - The desired export format.
 * @param {string} query - Optional query string for filtered export.
 */
export const exportReport = async (sessionId, type, query = '') => {
    let url;
    if (sessionId === 'filtered') {
        url = `${API_BASE_URL}/reports/export${type === 'pdf' ? '/pdf' : ''}${query}`;
    } else {
        url = `${API_BASE_URL}/reports/${sessionId}/export${type === 'pdf' ? '/pdf' : ''}`;
    }
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
        const response = await fetch(url, { headers });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Export failed.');
        }

        const blob = await response.blob();
        const disposition = response.headers.get('Content-Disposition') || '';
        const fileNameMatch = disposition.match(/filename[^;=\n]*=\s*(?:['"]?)([^'"\n;]+)(?:['"]?)/);
        const fileName = fileNameMatch?.[1]?.trim() || `report_${sessionId}.${type}`;
        
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);

    } catch (error) {
        console.error(`Export Error: ${error.message}`);
        throw error;
    }
};

export const getDashboardStats = () => request('/users/admin/dashboard-stats');
export const getRecentInterviews = () => request('/users/admin/recent-interviews');

export const updateProfile = (profileData) => request('/users/me', { method: 'PUT', body: JSON.stringify(profileData) });
export const changePassword = (data) => request('/users/me/password', { method: 'PUT', body: JSON.stringify(data) });
export const deleteAccount = () => request('/users/me', { method: 'DELETE' });

// Super Admin — Company endpoints
export const getAllCompanies = () => request('/companies');
export const getCompanyById = (companyId) => request(`/companies/${companyId}`);
export const getOverallStats = () => request('/companies/stats/overall');
export const createCompany = (companyData) => request('/companies', { method: 'POST', body: JSON.stringify(companyData) });
export const updateCompany = (companyId, companyData) => request(`/companies/${companyId}`, { method: 'PUT', body: JSON.stringify(companyData) });
export const toggleCompanyStatus = (companyId) => request(`/companies/${companyId}/toggle-status`, { method: 'POST' });
export const updateSubscription = (companyId, data) => request(`/companies/${companyId}/subscription`, { method: 'PUT', body: JSON.stringify(data) });

// Pricing Plan endpoints
export const getActivePlans = () => request('/pricing-plans');
export const getAllPlansForAdmin = () => request('/pricing-plans/manage');
export const createPlan = (planData) => request('/pricing-plans', { method: 'POST', body: JSON.stringify(planData) });
export const updatePlan = (planId, planData) => request(`/pricing-plans/${planId}`, { method: 'PUT', body: JSON.stringify(planData) });
export const deletePlan = (planId) => request(`/pricing-plans/${planId}`, { method: 'DELETE' });
export const reorderPlans = (order) => request('/pricing-plans/reorder', { method: 'POST', body: JSON.stringify({ order }) });

// Notification endpoints
export const getNotifications = () => request('/notifications');
export const markNotificationRead = (id) => request(`/notifications/${id}/read`, { method: 'PUT' });
export const markAllNotificationsRead = () => request('/notifications/read-all', { method: 'PUT' });
export const createUpgradeRequest = (data) => request('/notifications/upgrade-request', { method: 'POST', body: JSON.stringify(data) });
export const requestAddon = (data) => request('/notifications/addon-request', { method: 'POST', body: JSON.stringify(data) });

// Company admin — My Plan
export const getMyPlan = () => request('/companies/my-plan');
export const getMyUsageHistory = (limit = 24) => request(`/companies/my-usage-history?limit=${limit}`);
export const getCompanyUsageHistory = (companyId, limit = 24) =>
    request(`/companies/${companyId}/usage-history?limit=${limit}`);

// Super admin — Apply Add-On
export const applyAddon = (companyId, data) => request(`/companies/${companyId}/apply-addon`, { method: 'POST', body: JSON.stringify(data) });

// Resume endpoints
export const getResumeStatus   = (resumeId) => request(`/resumes/${resumeId}/status`);
export const bulkDecisionResumes = (resumeIds, decision, reason) =>
    request('/resumes/bulk-decision', { method: 'POST', body: JSON.stringify({ resumeIds, decision, reason }) });

export const bulkUploadResumes = async (formData) => {
    const url = `${API_BASE_URL}/resumes/bulk`;
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
        const response = await fetch(url, { method: 'POST', headers, body: formData });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        return response.json();
    } catch (error) {
        console.error(`API Client Error: ${error.message}`);
        throw error;
    }
};

export const uploadResume = async (formData) => {
    const url = `${API_BASE_URL}/resumes`;
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    try {
        const response = await fetch(url, { 
            method: 'POST', 
            headers, 
            body: formData 
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        return response.json();
    } catch (error) {
        console.error(`API Client Error: ${error.message}`);
        throw error;
    }
};

export const getResumes = (params = {}) => {
    const qs = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
    return request(`/resumes${qs ? '?' + qs : ''}`);
};
export const getResumeById = (resumeId) => request(`/resumes/${resumeId}`);
export const getResumeByCandidateId = (userId) => request(`/resumes/candidate/${userId}`);
export const deleteResume = (resumeId) => request(`/resumes/${resumeId}`, { method: 'DELETE' });
// `details` supplies a name/email that parsing could not read; they are saved
// onto the resume so they are never asked for twice.
export const selectCandidate = (resumeId, details) => {
    // Only the two fields the server reads are forwarded. Anything else — most
    // easily a React event from a handler wired directly to a button — would
    // throw on JSON.stringify before the request was even sent.
    const body = {};
    if (typeof details?.name  === 'string') body.name  = details.name;
    if (typeof details?.email === 'string') body.email = details.email;
    return request(`/resumes/${resumeId}/select`, { method: 'POST', body: JSON.stringify(body) });
};
export const assignResumeToExistingUser = (resumeId, userId) => request(`/resumes/${resumeId}/assign-user`, { method: 'POST', body: JSON.stringify({ userId }) });
export const rejectCandidate = (resumeId, reason) => request(`/resumes/${resumeId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });
/** Start AI parsing + ATS scoring for a resume that is still awaiting evaluation. */
export const parseResume = (resumeId) => request(`/resumes/${resumeId}/parse`, { method: 'POST' });
/** Queue several evaluations at once; the server drains them a few at a time. */
export const bulkParseResumes = (resumeIds) =>
    request('/resumes/bulk-parse', { method: 'POST', body: JSON.stringify({ resumeIds }) });

/**
 * Open the original upload in a new tab.
 * The file is behind auth, so it is fetched with the token and handed to the
 * browser as a blob rather than linked directly.
 */
export const openResumeFile = async (resumeId) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE_URL}/resumes/${resumeId}/file`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Could not open the resume.');
    }
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank', 'noopener');
    // Give the new tab time to take the blob before releasing it.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// ── Public job link (admin side) ────────────────────────────────────────────
export const generateJobPublicLink = (templateId) =>
    request(`/templates/${templateId}/public-link`, { method: 'POST' });
export const toggleJobPublicLink = (templateId, isPublic) =>
    request(`/templates/${templateId}/public-link`, { method: 'PATCH', body: JSON.stringify({ isPublic }) });

// ── Public job board (candidate side — no auth) ─────────────────────────────
export const getPublicJob = (slug) => request(`/public/jobs/${slug}`);

/**
 * Submit a public job application. Uses raw fetch because the body is
 * multipart/form-data and must not carry the JSON content-type header.
 */
export const applyToPublicJob = async (slug, { fullName, email, phone, resume, declaredSkills = [] }) => {
    const form = new FormData();
    form.append('fullName', fullName);
    form.append('email', email);
    if (phone) form.append('phone', phone);
    form.append('resume', resume);
    // Repeated keys arrive as an array server-side; a single one arrives as a
    // string, which the API handles.
    declaredSkills.forEach(s => form.append('declaredSkills', s));

    const response = await fetch(`${API_BASE_URL}/public/jobs/${slug}/apply`, {
        method: 'POST',
        body: form,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const err = new Error(data.message || 'Could not submit your application. Please try again.');
        err.status = response.status;
        err.code = data.code;
        throw err;
    }
    return data;
};

// ── Interview Masters (per-company lookup data: rounds, difficulty, types) ──
export const getMasters = (type) => request(`/masters${type ? `?type=${type}` : ''}`);
export const getPermissionsCatalog = () => request('/masters/permissions-catalog');
export const createMaster = (data) => request('/masters', { method: 'POST', body: JSON.stringify(data) });
export const updateMaster = (id, data) => request(`/masters/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const toggleMaster = (id) => request(`/masters/${id}/toggle`, { method: 'PATCH' });
// Persists a drag-and-drop ordering: orderedIds is the full list for that type.
export const reorderMasters = (type, orderedIds) =>
    request('/masters/reorder', { method: 'PATCH', body: JSON.stringify({ type, orderedIds }) });

// ── Candidate Process (multi-round pipeline) ─────────────────────────────────
export const listCandidateProcesses = (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return request(`/candidate-process${qs ? `?${qs}` : ''}`);
};
export const getCandidateProcess     = (processId)   => request(`/candidate-process/${processId}`);
export const generateConsolidatedReport = (processId) => request(`/candidate-process/${processId}/generate-report`, { method: 'POST' });
export const getProcessesByCandidate = (candidateId) => request(`/candidate-process/candidate/${candidateId}`);
export const setProcessDecision      = (processId, data) => request(`/candidate-process/${processId}/decision`, { method: 'PUT', body: JSON.stringify(data) });
export const getPipelineSummary      = ()            => request('/candidate-process/pipeline');

// Downloads the consolidated PDF as a binary blob and triggers a browser save dialog.
export const exportConsolidatedPDF = async (processId, filename = 'consolidated_report.pdf') => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE_URL}/candidate-process/${processId}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `PDF export failed (${res.status})`);
    }
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// ── Candidates for template (ATS-filtered, for scheduling) ───────────────────
export const getCandidatesForTemplate = (templateId, params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return request(`/interview/candidates-for-template/${templateId}${qs ? `?${qs}` : ''}`);
};
export const updateProcessRound = (sessionId, data) => request(`/interview/sessions/${sessionId}/update-process`, { method: 'POST', body: JSON.stringify(data) });

// ── Round Decision & Offer Letter ─────────────────────────────────────────────
export const setRoundDecision = (processId, roundNumber, data) =>
    request(`/candidate-process/${processId}/rounds/${roundNumber}/decision`, { method: 'POST', body: JSON.stringify(data) });
export const generateOfferLetter = (processId, details) =>
    request(`/candidate-process/${processId}/offer-letter`, { method: 'POST', body: JSON.stringify({ details }) });

// ── Interview / Company Settings ─────────────────────────────────────────────
export const getInterviewSettings = () => request('/companies/interview-settings');
export const updateInterviewSettings = (data) => request('/companies/interview-settings', { method: 'PUT', body: JSON.stringify(data) });

// ── Company profile (self-service, manage_settings) ─────────────────────────
/** Name and logo only — readable by any signed-in member of the company. */
export const getCompanyBranding   = () => request('/companies/branding');

// ── Profile picture ─────────────────────────────────────────────────────────
export const deleteAvatar = () => request('/users/me/avatar', { method: 'DELETE' });

export const uploadAvatar = async (file) => {
    const form = new FormData();
    form.append('avatar', file);
    const token = localStorage.getItem('token');

    const response = await fetch(`${API_BASE_URL}/users/me/avatar`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Could not upload the picture.');
    return data;
};
export const getCompanyProfile    = () => request('/companies/profile');
export const updateCompanyProfile = (data) =>
    request('/companies/profile', { method: 'PUT', body: JSON.stringify(data) });
export const deleteCompanyLogo    = () => request('/companies/profile/logo', { method: 'DELETE' });

// ── Company SMTP (password is write-only — GET never returns it) ────────────
export const getCompanyEmailConfig    = () => request('/companies/email-config');
export const updateCompanyEmailConfig = (data) =>
    request('/companies/email-config', { method: 'PUT', body: JSON.stringify(data) });
export const testCompanyEmailConfig   = (to) =>
    request('/companies/email-config/test', { method: 'POST', body: JSON.stringify({ to }) });
export const deleteCompanyEmailConfig = () => request('/companies/email-config', { method: 'DELETE' });

// ── Company email templates (candidate-facing copy) ─────────────────────────
export const getEmailTemplates   = () => request('/email-templates');
export const getEmailTemplate    = (type) => request(`/email-templates/${type}`);
export const updateEmailTemplate = (type, data) =>
    request(`/email-templates/${type}`, { method: 'PUT', body: JSON.stringify(data) });
export const resetEmailTemplate  = (type) =>
    request(`/email-templates/${type}`, { method: 'DELETE' });
export const previewEmailTemplate = (type, data) =>
    request(`/email-templates/${type}/preview`, { method: 'POST', body: JSON.stringify(data) });

/** Logo upload — multipart, so it bypasses the JSON request helper. */
export const uploadCompanyLogo = async (file) => {
    const form = new FormData();
    form.append('logo', file);
    const token = localStorage.getItem('token');

    const response = await fetch(`${API_BASE_URL}/companies/profile/logo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Could not upload the logo.');
    return data;
};

// ── Support Tickets ───────────────────────────────────────────────────────────
export const createSupportTicket = (data) =>
    request('/support', { method: 'POST', body: JSON.stringify(data) });
export const getSupportTickets = (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '')).toString();
    return request(`/support${qs ? `?${qs}` : ''}`);
};
export const getSupportTicketStats = () => request('/support/stats');
export const getSupportTicketById  = (id) => request(`/support/${id}`);
export const addTicketMessage      = (id, data) =>
    request(`/support/${id}/messages`, { method: 'POST', body: JSON.stringify(data) });
export const updateTicketStatus    = (id, status) =>
    request(`/support/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });

// ── Code execution ────────────────────────────────────────────────────────────
export const executeCode = (data) =>
    request('/code/execute', { method: 'POST', body: JSON.stringify(data) });
export const getSupportedLanguages = () => request('/code/languages');

// ── Disconnect / rejoin ───────────────────────────────────────────────────────
// Candidate heartbeat — keeps session alive; auto-resumes if disconnected
export const heartbeatSession = (sessionId) =>
    request(`/interview/sessions/${sessionId}/heartbeat`, { method: 'PUT' });

// Admin: reset a stuck/disconnected session
export const resetSession = (sessionId, data) =>
    request(`/interview/sessions/${sessionId}/reset`, { method: 'PUT', body: JSON.stringify(data) });

// Candidate: request a reschedule after losing connection
export const requestReschedule = (sessionId, data = {}) =>
    request(`/interview/sessions/${sessionId}/request-reschedule`, { method: 'POST', body: JSON.stringify(data) });

// ── Question Sets ─────────────────────────────────────────────────────────────

/** Generate a new question set via AI and save it. */
export const createQuestionSet = (data) =>
    request('/question-sets', { method: 'POST', body: JSON.stringify(data) });

/** List sets for the company. params: { templateId?, interviewTypeName?, difficultyName? } */
export const getQuestionSets = (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null)));
    return request(`/question-sets${qs.toString() ? `?${qs}` : ''}`);
};

/** Get full set with all question bodies. */
export const getQuestionSet = (id) => request(`/question-sets/${id}`);

/** Update name / description only. */
export const updateQuestionSetMeta = (id, data) =>
    request(`/question-sets/${id}`, { method: 'PUT', body: JSON.stringify(data) });

/** Replace the entire questions array (save after bulk editing). */
export const updateQuestionSetQuestions = (id, questions) =>
    request(`/question-sets/${id}/questions`, { method: 'PUT', body: JSON.stringify({ questions }) });

/** Patch a single question (timeLimitSeconds, text, etc.). */
export const updateSingleQuestion = (id, qId, data) =>
    request(`/question-sets/${id}/questions/${qId}`, { method: 'PATCH', body: JSON.stringify(data) });

/** AI regenerates one replacement question in-place. */
export const regenerateSingleQuestion = (id, qId) =>
    request(`/question-sets/${id}/questions/${qId}/regenerate`, { method: 'POST' });

/** AI regenerates ALL questions in the set (full replacement using same params). */
export const regenerateAllQuestions = (id) =>
    request(`/question-sets/${id}/regenerate-all`, { method: 'POST' });

/** Duplicate an existing set. */
export const duplicateQuestionSet = (id, data = {}) =>
    request(`/question-sets/${id}/duplicate`, { method: 'POST', body: JSON.stringify(data) });

/** Soft-delete a question set. */
export const deleteQuestionSet = (id) =>
    request(`/question-sets/${id}`, { method: 'DELETE' });

/** Parse a PDF/DOCX/TXT file server-side; returns questions array for review (does not save). */
export const parseUploadedQuestionSet = async (formData) => {
    const url = `${API_BASE_URL}/question-sets/upload-parse`;
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // No Content-Type header — browser sets multipart/form-data + boundary automatically
    const response = await fetch(url, { method: 'POST', headers, body: formData });
    if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Upload failed.' }));
        throw new Error(err.message || 'Upload failed.');
    }
    return response.json();
};

/** Save a reviewed uploaded question set. */
export const saveUploadedQuestionSet = (data) =>
    request('/question-sets/from-upload', { method: 'POST', body: JSON.stringify(data) });

/** Download the sample question set TXT file. */
export const getQuestionSetSampleFileUrl = (format = 'txt') => {
    const base = (typeof window !== 'undefined' && window.__ECHOHIRE_API_URL__)
        || import.meta.env?.VITE_API_BASE_URL
        || '/api';
    return `${base}/question-sets/sample-file?format=${format}`;
};

// ── Question Regeneration ─────────────────────────────────────────────────────

/**
 * Replace all questions on a scheduled session.
 * @param {string} sessionId
 * @param {{ mode: 'dynamic'|'from_set', notifyCandidate: boolean }} data
 */
export const regenerateSessionQuestions = (sessionId, data) =>
    request(`/interview/sessions/${sessionId}/regenerate-questions`, {
        method: 'POST',
        body: JSON.stringify(data),
    });

// ── Chunked audio upload + Groq transcription ─────────────────────────────────

export const uploadAudioChunk = async (chunkBlob, { sessionId, questionId, chunkIndex, mimeType }) => {
    const token = localStorage.getItem('token');
    const ext = (mimeType || '').includes('ogg') ? 'ogg' : (mimeType || '').includes('mp4') ? 'mp4' : 'webm';
    const fd = new FormData();
    fd.append('chunk', chunkBlob, `chunk-${chunkIndex}.${ext}`);
    const qs = `?sessionId=${encodeURIComponent(sessionId)}&questionId=${encodeURIComponent(questionId)}&chunkIndex=${chunkIndex}`;
    const res = await fetch(`${API_BASE_URL}/interview/audio-chunk${qs}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
    });
    if (!res.ok) throw new Error(`Chunk upload failed: HTTP ${res.status}`);
    return res.json();
};

export const assembleAndTranscribe = async ({ sessionId, questionId, totalChunks, mimeType }) => {
    return request('/interview/audio-assemble', {
        method: 'POST',
        body: JSON.stringify({ sessionId, questionId, totalChunks, mimeType }),
    });
};

export const retryResponseTranscription = (sessionId, responseId) =>
    request(`/interview/sessions/${sessionId}/responses/${responseId}/retry-transcription`, { method: 'POST' });

// ── Leads (super admin) ───────────────────────────────────────────────────────

export const getLeads = (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')));
    return request(`/leads${qs.toString() ? `?${qs}` : ''}`);
};

export const getLeadById = (id) => request(`/leads/${id}`);

export const getLeadStats = () => request('/leads/stats');

export const getNewLeadsCount = () => request('/leads/new-count');

export const updateLeadStatus = (id, data) =>
    request(`/leads/${id}/status`, { method: 'PUT', body: JSON.stringify(data) });

export const updateLeadNotes = (id, notes) =>
    request(`/leads/${id}/notes`, { method: 'PUT', body: JSON.stringify({ notes }) });

export const editLead = (id, data) =>
    request(`/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteLead = (id) =>
    request(`/leads/${id}`, { method: 'DELETE' });

export const sendLeadEmail = (id, data) =>
    request(`/leads/${id}/send-email`, { method: 'POST', body: JSON.stringify(data) });

export const onboardLead = (id, data) =>
    request(`/leads/${id}/onboard`, { method: 'POST', body: JSON.stringify(data) });

// Sends the canonical email for a pipeline stage on demand, independently of
// the lead's current stage.
export const sendLeadStageEmail = (id, stage) =>
    request(`/leads/${id}/send-stage-email`, { method: 'POST', body: JSON.stringify({ stage }) });

// Renders the exact email a stage would send, without sending it.
export const previewLeadStageEmail = (id, stage) =>
    request(`/leads/${id}/preview-stage-email`, { method: 'POST', body: JSON.stringify({ stage }) });

export const updateLeadDemo = (id, data) =>
    request(`/leads/${id}/demo`, { method: 'PUT', body: JSON.stringify(data) });

export const markLeadSpam = (id, { isSpam, reason = '' }) =>
    request(`/leads/${id}/spam`, { method: 'POST', body: JSON.stringify({ isSpam, reason }) });

export const logLeadActivity = (id, data) =>
    request(`/leads/${id}/activity`, { method: 'POST', body: JSON.stringify(data) });

export const getLeadsExportUrl = (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v)));
    const token = localStorage.getItem('token');
    return `${API_BASE_URL}/leads/export${qs.toString() ? `?${qs}` : ''}&_token=${token}`;
};

export const exportLeads = async (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v)));
    const token = localStorage.getItem('token');
    const url = `${API_BASE_URL}/leads/export${qs.toString() ? `?${qs}` : ''}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `echohire-leads-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
};

// ── Prospect Leads (outbound / scraped) ──────────────────────────────────────
export const upsertProspectLeads = (data) =>
    request('/prospect-leads', { method: 'POST', body: JSON.stringify(data) });

export const getProspectLeads = (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '')));
    return request(`/prospect-leads${qs.toString() ? `?${qs}` : ''}`);
};

export const getProspectLeadStats = () =>
    request('/prospect-leads/stats');

export const getProspectLead = (id) =>
    request(`/prospect-leads/${id}`);

export const updateProspectLead = (id, data) =>
    request(`/prospect-leads/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteProspectLead = (id) =>
    request(`/prospect-leads/${id}`, { method: 'DELETE' });

export const sendProspectOutreach = (id, data) =>
    request(`/prospect-leads/${id}/outreach`, { method: 'POST', body: JSON.stringify(data) });

export const logProspectFollowUp = (id, data) =>
    request(`/prospect-leads/${id}/followup`, { method: 'POST', body: JSON.stringify(data) });

export const addProspectContactLog = (id, data) =>
    request(`/prospect-leads/${id}/log`, { method: 'POST', body: JSON.stringify(data) });

export const sendProspectDemoEmail = (id, data) =>
    request(`/prospect-leads/${id}/demo-email`, { method: 'POST', body: JSON.stringify(data) });

export const getProspectSuggestion = (id) =>
    request(`/prospect-leads/${id}/suggestion`);

export const updateProspectNextAction = (id, data) =>
    request(`/prospect-leads/${id}/next-action`, { method: 'PUT', body: JSON.stringify(data) });

// ── Bulk + sequence actions ───────────────────────────────────────────────────
export const bulkEmailProspects = (data) =>
    request('/prospect-leads/bulk-email', { method: 'POST', body: JSON.stringify(data) });

export const verifyProspectEmails = (ids) =>
    request('/prospect-leads/verify-emails', { method: 'POST', body: JSON.stringify({ ids }) });

export const startProspectSequence = (id, sequenceId) =>
    request(`/prospect-leads/${id}/sequence/start`, { method: 'POST', body: JSON.stringify(sequenceId ? { sequenceId } : {}) });

export const stopProspectSequence = (id, reason = 'manual') =>
    request(`/prospect-leads/${id}/sequence/stop`, { method: 'POST', body: JSON.stringify({ reason }) });

// ── Email template CRUD ──────────────────────────────────────────────────────
export const getProspectEmailTemplates = () =>
    request('/prospect-email-templates');

export const getProspectEmailTemplate = (type) =>
    request(`/prospect-email-templates/${type}`);

export const createProspectEmailTemplate = (data) =>
    request('/prospect-email-templates', { method: 'POST', body: JSON.stringify(data) });

export const updateProspectEmailTemplate = (type, data) =>
    request(`/prospect-email-templates/${type}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteProspectEmailTemplate = (type) =>
    request(`/prospect-email-templates/${type}`, { method: 'DELETE' });

export const reorderProspectDripTemplates = (order) =>
    request('/prospect-email-templates/reorder', { method: 'POST', body: JSON.stringify({ order }) });

export const sendTestProspectEmail = (type, toEmail) =>
    request(`/prospect-email-templates/${type}/test`, { method: 'POST', body: JSON.stringify(toEmail ? { toEmail } : {}) });

// ── Drip Sequences CRUD ───────────────────────────────────────────────────────
export const getProspectDripSequences = () =>
    request('/prospect-drip-sequences');

export const createProspectDripSequence = (data) =>
    request('/prospect-drip-sequences', { method: 'POST', body: JSON.stringify(data) });

export const updateProspectDripSequence = (id, data) =>
    request(`/prospect-drip-sequences/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteProspectDripSequence = (id) =>
    request(`/prospect-drip-sequences/${id}`, { method: 'DELETE' });

export const setDefaultDripSequence = (id) =>
    request(`/prospect-drip-sequences/${id}/set-default`, { method: 'POST' });

// ── Template variable CRUD ────────────────────────────────────────────────────
export const getProspectTemplateVariables = () =>
    request('/prospect-template-variables');

export const createProspectTemplateVariable = (data) =>
    request('/prospect-template-variables', { method: 'POST', body: JSON.stringify(data) });

export const updateProspectTemplateVariable = (id, data) =>
    request(`/prospect-template-variables/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteProspectTemplateVariable = (id) =>
    request(`/prospect-template-variables/${id}`, { method: 'DELETE' });


// ── System Health ─────────────────────────────────────────────────────────────
export const getHealthSystem  = () => request('/health/system');
export const getHealthDb      = () => request('/health/db');
export const getHealthJobs    = () => request('/health/jobs');
export const getHealthStorage = () => request('/health/storage');
export const getHealthKpis    = () => request('/health/kpis');

// ── Internal Team (echohire_staff management) ─────────────────────────────────
export const getInternalTeam      = () => request('/internal-team');
export const getInternalMember    = (id) => request(`/internal-team/${id}`);
export const createInternalMember = (data) => request('/internal-team', { method: 'POST', body: JSON.stringify(data) });
export const updateInternalMember = (id, data) => request(`/internal-team/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteInternalMember = (id) => request(`/internal-team/${id}`, { method: 'DELETE' });
export const resetMemberPassword  = (id) => request(`/internal-team/${id}/reset-password`, { method: 'POST' });


// ── Billing settings & company billing details ────────────────────────────────
export const getBillingSettings    = () => request('/billing/settings');
export const updateBillingSettings = (data) => request('/billing/settings', { method: 'PUT', body: JSON.stringify(data) });

export const getCompanyBilling    = (companyId) => request(`/billing/companies/${companyId}`);
export const updateCompanyBilling = (companyId, data) =>
    request(`/billing/companies/${companyId}`, { method: 'PUT', body: JSON.stringify(data) });
export const sendBillingEmailVerification = (companyId) =>
    request(`/billing/companies/${companyId}/verify-email`, { method: 'POST' });

// Public — reached from the link in the verification email, so no token needed.
export const confirmBillingEmail = (companyId, token) =>
    request('/billing/verify-email', { method: 'POST', body: JSON.stringify({ companyId, token }) });

// ── Orders ────────────────────────────────────────────────────────────────────
export const createOrder  = (data) => request('/orders', { method: 'POST', body: JSON.stringify(data) });
export const listOrders   = (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '')).toString();
    return request(`/orders${qs ? `?${qs}` : ''}`);
};
export const getOrder     = (id) => request(`/orders/${id}`);
export const updateOrder  = (id, data) => request(`/orders/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const confirmOrder = (id) => request(`/orders/${id}/confirm`, { method: 'POST' });
export const reopenOrder  = (id) => request(`/orders/${id}/reopen`, { method: 'POST' });
export const cancelOrder  = (id, reason) => request(`/orders/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) });

export const issueInvoiceForOrder = (orderId, data = {}) =>
    request(`/orders/${orderId}/invoice`, { method: 'POST', body: JSON.stringify(data) });
export const activateOrderWithoutPayment = (orderId, note) =>
    request(`/orders/${orderId}/activate`, { method: 'POST', body: JSON.stringify({ note }) });

// ── Invoices ──────────────────────────────────────────────────────────────────
export const listInvoices = (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '')).toString();
    return request(`/invoices${qs ? `?${qs}` : ''}`);
};
export const getInvoice        = (id) => request(`/invoices/${id}`);
export const emailInvoice      = (id) => request(`/invoices/${id}/email`, { method: 'POST' });
export const skipInvoiceEmail  = (id) => request(`/invoices/${id}/skip-email`, { method: 'POST' });
export const recordInvoicePayment = (id, data) =>
    request(`/invoices/${id}/payments`, { method: 'POST', body: JSON.stringify(data) });
export const cancelInvoice     = (id, reason) =>
    request(`/invoices/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) });

/**
 * Downloads an invoice PDF.
 *
 * Fetched as a blob rather than opened in a new tab: the endpoint needs the
 * Authorization header, which a plain window.open cannot carry.
 */
export const downloadInvoicePdf = async (id, invoiceNumber = 'invoice') => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE_URL}/invoices/${id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Could not download this invoice.');

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${String(invoiceNumber).replace(/\//g, '-')}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

// ── Company suspension ────────────────────────────────────────────────────────
export const setCompanySuspension = (companyId, suspended, reason = '') =>
    request(`/companies/${companyId}/suspension`, { method: 'PUT', body: JSON.stringify({ suspended, reason }) });

// ── Company's own invoices (read only, scoped server-side by token) ───────────
export const getMyInvoices = () => request('/invoices/mine');

export const downloadMyInvoicePdf = async (id, invoiceNumber = 'invoice') => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE_URL}/invoices/mine/${id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Could not download this invoice.');

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${String(invoiceNumber).replace(/\//g, '-')}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

// Company's own billing identity (read only, scoped server-side by token).
export const getMyBilling = () => request('/billing/mine');

// Amend an invoice that has not been sent or part paid.
export const updateInvoice = (id, data) =>
    request(`/invoices/${id}`, { method: 'PUT', body: JSON.stringify(data) });

// Billing context for a company: dates, cover, open orders and invoices.
export const getOrderContext = (companyId) => request(`/orders/context/${companyId}`);

// Resume pipeline analytics for one company, with platform context.
export const getCompanyResumeAnalytics = (companyId, days = 'all') =>
    request(`/companies/${companyId}/resume-analytics?days=${encodeURIComponent(days)}`);

// Resend a candidate's interview invitation, optionally with fresh credentials.
export const resendSessionInvite = (sessionId, resetPassword = true) =>
    request(`/interview/sessions/${sessionId}/resend-invite`, {
        method: 'POST',
        body: JSON.stringify({ resetPassword }),
    });

/**
 * Download a candidate's resume as a typeset PDF built from the parsed data.
 * Only available once the resume's evaluation has completed.
 *
 * @param {string} resumeId
 * @param {{ evaluation?: boolean }} opts  true adds EchoHire's evaluation pages
 */
export const downloadResumePdf = async (resumeId, { evaluation = false } = {}) => {
  const url = `${API_BASE_URL}/resumes/${resumeId}/pdf${evaluation ? '?evaluation=1' : ''}`;
  const token = localStorage.getItem('token');
  const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

  if (!response.ok) {
    // The server answers errors as JSON; surface its message, not a raw body.
    let message = 'Could not download the resume.';
    try { message = (await response.json())?.message || message; } catch { /* not JSON */ }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const name = disposition.match(/filename="?([^";\n]+)"?/)?.[1]?.trim()
    || `resume${evaluation ? '-and-evaluation' : ''}.pdf`;

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking in the same tick can cancel the download in Firefox; give the
  // browser a moment to take hold of the blob first.
  setTimeout(() => URL.revokeObjectURL(link.href), 10000);
};
